import React, { ReactNode, useCallback, useState } from 'react';
import classNames from 'classnames';
import { ClickAwayListener } from '../../ClickAwayListener';

export interface ISelectProps<T> {
  className?: string;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  value?: T;
  options: T[];
  label?: ReactNode;
  labelClass?: string;
  placeholder?: ReactNode;
  dropdownClass?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  helperClass?: string;
  getOptionLabel?: (option: T) => ReactNode;
  onChange?: (option: T) => void;
}

export function Select<T>({
  className,
  fullWidth,
  size = 'md',
  value,
  options,
  getOptionLabel,
  label,
  labelClass,
  placeholder,
  dropdownClass,
  disabled,
  error,
  helperText,
  helperClass,
  onChange,
}: ISelectProps<T>) {
  const [opened, setOpened] = useState(false);
  const [focused, setFocused] = useState(false);

  const wrapperClassName = classNames('relative', fullWidth && 'w-full', className);

  const buttonClassName = classNames(
    'w-full flex items-center justify-between border outline-none transition-all',
    disabled
      ? 'bg-white/16 text-white/48 border-white/32'
      : [
          'bg-input-bg text-input-text',
          error ? 'border-danger' : focused ? 'border-input-border-focus' : 'border-input-border',
        ],
    size === 'sm' && ['min-h-9 text-17p rounded-10p px-2 py-1.5 gap-1.5'],
    size === 'md' && ['min-h-11 text-17p rounded-10p px-4 py-2.5 gap-2'],
  );

  const labelClassName = classNames(
    'block font-semibold mb-1',
    size === 'md' && 'text-15p',
    error ? 'text-danger' : 'text-typo1',
    labelClass,
  );

  const helperClassName = classNames(
    'mt-1',
    size === 'md' && 'text-sm',
    error ? 'text-danger' : 'text-typo1',
    helperClass,
  );

  const dropdownMenuClass = classNames(
    'absolute z-10 left-0 w-full bg-menu-bg text-menu-text shadow overflow-hidden',
    size === 'md' && 'rounded-lg',
    dropdownClass,
  );

  const getMenuItemClass = (option: T) => {
    return classNames(
      'transition-all cursor-pointer p-3 text-left',
      'hover:bg-menu-bg-hover',
      size === 'md' && 'px-4 py-2',
      option === value && 'text-menu-active',
    );
  };

  const getLabel = (option: T) => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    return option?.toString();
  };

  const onSelectOption = (option: T) => {
    if (disabled) {
      return;
    }
    onChange && onChange(option);
    setOpened(false);
    setFocused(false);
  };

  const onCloseDropdown = useCallback(() => {
    setOpened(false);
    setFocused(false);
  }, []);

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setOpened(!opened);
    setFocused(!opened);
  };

  return (
    <ClickAwayListener className={wrapperClassName} onClick={onCloseDropdown}>
      {label && <label className={labelClassName}>{label}</label>}
      <button
        type="button"
        className={buttonClassName}
        onClick={handleToggle}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          if (!opened) {
            setFocused(false);
          }
        }}
      >
        <span className="flex-grow text-left">
          {value ? getLabel(value) : <span className="text-typo2">{placeholder}</span>}
        </span>
        <i className={classNames('fa fa-angle-down transition-all', opened && 'rotate-180')} />
      </button>
      {opened && (
        <div className={dropdownMenuClass}>
          {options.map((option, i) => (
            <div key={i} className={getMenuItemClass(option)} onClick={() => onSelectOption(option)}>
              {getLabel(option)}
            </div>
          ))}
        </div>
      )}
      {helperText && <div className={helperClassName}>{helperText}</div>}
    </ClickAwayListener>
  );
}
